import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HomePage from "./pages/HomePage";
import Login from "./components/Login";
import RequireAuth from "./components/auth/RequireAuth";
import AccessDenied from "./components/auth/AccessDenied";
import withTailwindPage from "./components/generic/withTailwindPage";
import PageLoader from "./components/generic/PageLoader";
import { NavigationLockProvider } from "./context/NavigationLockContext";
import { isUsabilityTestsEnabled } from "./config/featureFlags";

const TaoluDetailsPage = lazy(() => import("./pages/TaoluDetailsPage"));
const ChinWooDetailsPage = lazy(() => import("./pages/ChinWooDetailsPage"));
const SponsorDetailsPage = lazy(() => import("./pages/SponsorDetailsPage"));
const SponsorsPartnersPage = lazy(() => import("./pages/SponsorsPartnersPage"));
const PoliciesPage = lazy(() => import("./pages/PoliciesPage"));
const DownloadsPage = lazy(() => import("./pages/DownloadsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const UsabilityTestWizardPage = lazy(() => import("./pages/UsabilityTestWizardPage"));

const AdminTailwindDashboard = lazy(() => import("./pages/AdminTailwindDashboard"));
const ScorekeeperView = lazy(() => import("./components/scorekeeper/ScorekeeperView"));
const LiveResultsView = lazy(() => import("./components/scorekeeper/LiveResultsView"));
const CallerView = lazy(() => import("./components/caller/CallerView"));
const PublicResults = lazy(() => import("./components/public/PublicResults"));
const PublicMedalTable = lazy(() => import("./components/public/PublicMedalTable"));
const PublicMVPTable = lazy(() => import("./components/public/PublicMVPTable"));

function lazyTailwindPage(importFn) {
  return lazy(async () => {
    const mod = await importFn();
    return { default: withTailwindPage(mod.default) };
  });
}

const CategoriesTailwindPage = lazyTailwindPage(() => import("./components/admin/CategoriesTailwind"));
const GroupsTailwindPage = lazyTailwindPage(() => import("./components/admin/GroupsTailwind"));
const ScheduleTailwindPage = lazyTailwindPage(() => import("./components/admin/ScheduleTailwind"));
const ScheduleBlockPreviewPage = lazyTailwindPage(() => import("./components/admin/ScheduleBlockPreview"));
const ScheduleSettingsTailwindPage = lazyTailwindPage(() => import("./components/admin/ScheduleSettingsTailwind"));
const DataManagementTailwindPage = lazyTailwindPage(() => import("./components/admin/DataManagementTailwind"));
const MedalTableTailwindPage = lazyTailwindPage(() => import("./components/admin/MedalTableTailwind"));
const MVPTableTailwindPage = lazyTailwindPage(() => import("./components/admin/MVPTableTailwind"));
const ResultsTailwindPage = lazyTailwindPage(() => import("./components/admin/ResultsTailwind"));
const UsersTailwindPage = lazyTailwindPage(() => import("./components/admin/UsersTailwind"));
const UserRolesTailwindPage = lazyTailwindPage(() => import("./components/admin/UserRolesTailwind"));
const ParticipantsTailwindPage = lazyTailwindPage(() => import("./components/admin/ParticipantsTailwind"));
const NationsTailwindPage = lazyTailwindPage(() => import("./components/admin/NationsTailwind"));
const UsabilityTasksTailwindPage = lazyTailwindPage(() => import("./components/admin/UsabilityTasksTailwind"));
const UsabilityOpenQuestionsTailwindPage = lazyTailwindPage(() =>
  import("./components/admin/UsabilityOpenQuestionsTailwind")
);
const UsabilityEvaluationsTailwindPage = lazyTailwindPage(() =>
  import("./components/admin/UsabilityEvaluationsTailwind")
);
const UsabilityStatisticsTailwindPage = lazyTailwindPage(() =>
  import("./components/admin/UsabilityStatisticsTailwind")
);

const ALLOWED_LANGUAGES = ["de", "en", "zh"];

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedLang = params.get("lang");
    const lang = ALLOWED_LANGUAGES.includes(requestedLang) ? requestedLang : "de";
    i18n.changeLanguage(lang);
  }, [i18n]);

  return (
    <NavigationLockProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />

            <Route path="/admin" element={<Login />} />
            <Route
              path="/admin/dashboard"
              element={
                <RequireAuth roles={["admin", "scorekeeper", "caller"]}>
                  <AdminTailwindDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <RequireAuth permissions={["manage_categories"]}>
                  <CategoriesTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/participants"
              element={
                <RequireAuth permissions={["manage_participants"]}>
                  <ParticipantsTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/nations"
              element={
                <RequireAuth permissions={["manage_nations"]}>
                  <NationsTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/groups"
              element={
                <RequireAuth permissions={["manage_groups"]}>
                  <GroupsTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/schedule"
              element={
                <RequireAuth permissions={["manage_schedule"]}>
                  <ScheduleTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/schedule/block-preview"
              element={
                <RequireAuth permissions={["manage_schedule"]}>
                  <ScheduleBlockPreviewPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/schedule-settings"
              element={
                <RequireAuth permissions={["manage_schedule_settings"]}>
                  <ScheduleSettingsTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/data-management"
              element={
                <RequireAuth permissions={["manage_data"]}>
                  <DataManagementTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/medal-table"
              element={
                <RequireAuth permissions={["view_results"]}>
                  <MedalTableTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/mvp-table"
              element={
                <RequireAuth permissions={["view_results"]}>
                  <MVPTableTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/results"
              element={
                <RequireAuth permissions={["view_results", "manage_results"]}>
                  <ResultsTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/scorekeeper"
              element={
                <RequireAuth roles={["scorekeeper", "admin", "superadmin"]}>
                  <ScorekeeperView />
                </RequireAuth>
              }
            />
            <Route
              path="/caller"
              element={
                <RequireAuth permissions={["view_caller"]}>
                  <CallerView />
                </RequireAuth>
              }
            />
            <Route path="/live-results" element={<LiveResultsView />} />
            <Route
              path="/admin/user-roles"
              element={
                <RequireAuth permissions={["manage_roles"]}>
                  <UserRolesTailwindPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireAuth roles={["superadmin"]}>
                  <UsersTailwindPage />
                </RequireAuth>
              }
            />
            {isUsabilityTestsEnabled && (
              <>
                <Route
                  path="/admin/usability/tasks"
                  element={
                    <RequireAuth roles={["superadmin"]}>
                      <UsabilityTasksTailwindPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/usability/open-questions"
                  element={
                    <RequireAuth roles={["superadmin"]}>
                      <UsabilityOpenQuestionsTailwindPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/usability/evaluations"
                  element={
                    <RequireAuth roles={["superadmin"]}>
                      <UsabilityEvaluationsTailwindPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/usability/statistics"
                  element={
                    <RequireAuth roles={["superadmin"]}>
                      <UsabilityStatisticsTailwindPage />
                    </RequireAuth>
                  }
                />
              </>
            )}

            <Route path="/access-denied" element={<AccessDenied />} />

            <Route path="/taolu" element={<TaoluDetailsPage />} />
            <Route path="/chinwoo" element={<ChinWooDetailsPage />} />
            <Route path="/sponsors" element={<SponsorDetailsPage />} />
            <Route path="/sponsors-partners" element={<SponsorsPartnersPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/downloads" element={<DownloadsPage />} />

            <Route path="/results" element={<PublicResults />} />
            <Route path="/medal-table" element={<PublicMedalTable />} />
            <Route path="/mvp-table" element={<PublicMVPTable />} />

            {isUsabilityTestsEnabled && (
              <Route path="/usability-tests" element={<UsabilityTestWizardPage />} />
            )}

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Router>
    </NavigationLockProvider>
  );
}

export default App;
